import { useState, useEffect } from 'react';
import { Volume2, Mic, Square, ArrowLeft, ArrowRight, CheckCircle2, Trophy, Zap, Clock } from 'lucide-react';
import { showAchievement, launchConfetti } from '../utils/effects.js';
import './Exercise.css';
import './PronunciationExercise.css';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const recognition = SpeechRecognition ? new SpeechRecognition() : null;
if (recognition) {
  recognition.continuous = false;
  recognition.lang = 'en-US';
  recognition.interimResults = false;
}

const levenshtein = (s1, s2) => {
  if (s1.length < s2.length) { return levenshtein(s2, s1); }
  if (s2.length === 0) { return s1.length; }
  let previousRow = [...Array(s2.length + 1).keys()];
  for (let i = 0; i < s1.length; i++) {
    let currentRow = [i + 1];
    for (let j = 0; j < s2.length; j++) {
      let insertions = previousRow[j + 1] + 1;
      let deletions = currentRow[j] + 1;
      let substitutions = previousRow[j] + (s1[i] !== s2[j] ? 1 : 0);
      currentRow.push(Math.min(insertions, deletions, substitutions));
    }
    previousRow = currentRow;
  }
  return previousRow[previousRow.length - 1];
};

function PronunciationExercise({ exerciseData, onCorrectAnswer }) {
  const { title, instruction, words_or_sentences: words = [], timeLimit = 180 } = exerciseData || {};

  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [result, setResult] = useState(null);
  
  const [failedAttempts, setFailedAttempts] = useState(0);

  const [points, setPoints] = useState(0);
  const [streak, setStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [isTimerActive, setIsTimerActive] = useState(true);

  const currentWord = words[currentWordIndex];
  const totalWords = words.length;
  const progressPercentage = totalWords > 0 ? ((currentWordIndex + 1) / totalWords) * 100 : 0;

  useEffect(() => {
    if(!isTimerActive) return;
    const timer = setInterval(() => {
        setTimeLeft(prev => {
            if (prev <= 1) {
                clearInterval(timer);
                setIsTimerActive(false);
                return 0;
            }
            return prev - 1;
        });
    }, 1000);
    return () => clearInterval(timer);
  }, [isTimerActive]);

  const cleanText = (text) => {
    if (!text) return '';
    return text.trim().toLowerCase().replace(/[.,!?;:"']/g, '').replace(/\s+/g, ' ').trim();
  };

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      window.speechSynthesis.speak(utterance);
    } else {
      alert("Lo siento, tu navegador no soporta la síntesis de voz.");
    }
  };

  const handleToggleListening = () => {
    if (!recognition) {
      alert("Lo siento, tu navegador no soporta el reconocimiento de voz.");
      return;
    }
    if (isListening) {
      recognition.stop();
    } else {
      setTranscript('');
      setResult(null);
      recognition.start();
    }
  };

  useEffect(() => {
    if (!recognition || !currentWord) return;
    
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    
    recognition.onresult = (event) => {
      // Captura el transcript real para usarlo en caso de fallo
      const currentTranscript = event.results[0][0].transcript;
      
      const cleanedTranscript = cleanText(currentTranscript);
      
      // ######################################################################
      // ### LÓGICA DE VALIDACIÓN CON ROBUSTEZ Y REGLA DE 3 INTENTOS ###
      // ######################################################################
      
      const targetText = currentWord.text;
      // Incluye el array 'alternates' del JSON para mayor robustez
      const alternates = currentWord.alternates || [];
      const possibleTargets = [targetText, ...alternates];
      let isCorrect = false;

      // 1. Comprobación de Levenshtein (Robustez)
      for (const target of possibleTargets) {
          const cleanedTarget = cleanText(target);
          const distance = levenshtein(cleanedTranscript, cleanedTarget);

          // Lógica de umbral de error permisivo
          let threshold;
          if (cleanedTarget.length <= 4) {
              threshold = 1;
          } else if (cleanedTarget.length <= 10) {
              threshold = 2;
          } else {
              threshold = 3;
          }
          
          if (distance <= threshold) {
              isCorrect = true;
              break;
          }
      }

      // 2. Comprobación de Fallo Forzado (Regla de 3 Intentos)
      const isForcedCorrect = !isCorrect && failedAttempts >= 2;

      if (isCorrect || isForcedCorrect) {
          // <<< IMPLEMENTACIÓN DE CONFIANZA: MUESTRA EL TEXTO OBJETIVO >>>
          setTranscript(targetText); 
          setResult('correct');
          setPoints(p => p + 10);
          setStreak(s => s + 1);
          setFailedAttempts(0); // Resetea intentos al pasar
      } else {
          // Si falla, mostramos el transcript real (raw attempt)
          setTranscript(currentTranscript); 
          setResult('incorrect');
          setStreak(0);
          setFailedAttempts(p => p + 1); // Incrementa intentos fallidos
      }
    };
    
    recognition.onerror = (event) => console.error("Speech recognition error:", event.error);
    
    return () => {
      recognition.onstart = null;
      recognition.onend = null;
      recognition.onresult = null;
      recognition.onerror = null;
    }
  }, [currentWord, failedAttempts]);

  const handleNext = () => {
    if (currentWordIndex < words.length - 1) {
      setCurrentWordIndex(currentWordIndex + 1);
      setTranscript('');
      setResult(null);
      setFailedAttempts(0); // Resetea intentos al cambiar de palabra
    } else {
      setIsTimerActive(false);
      onCorrectAnswer();
      showAchievement('¡Práctica de pronunciación completada!', '🗣️');
      launchConfetti();
    }
  };
  
  const handlePrevious = () => {
    if (currentWordIndex > 0) {
      setCurrentWordIndex(currentWordIndex - 1);
      setTranscript('');
      setResult(null);
      setFailedAttempts(0); // Resetea intentos al cambiar de palabra
    }
  };

  if (!words || words.length === 0) {
    return <div className="exercise-container">No se encontraron palabras para practicar.</div>;
  }
  if (!recognition) {
    return <div className="exercise-container">Lo siento, tu navegador no es compatible con los ejercicios de pronunciación.</div>;
  }

  return (
    <div className="exercise-container">
      <div className="pe-info-bar">
        <div className="pe-info-item"><span>🏆</span> {points} pts</div>
        <div className="pe-info-sub-bar">
            <div className="pe-info-item"><span>⚡</span> Racha: {streak}</div>
            <div className={`pe-info-item ${timeLeft <= 10 ? 'accent-bg' : ''}`}><span>🕒</span> {timeLeft}s</div>
        </div>
      </div>
      <div className="pe-progress-bar-container">
        <div className="pe-progress-bar-fill" style={{ width: `${progressPercentage}%` }}></div>
      </div>

      <div className="pe-header-card">
        <h2 className="exercise-title">{title || 'Práctica de Pronunciación'}</h2>
        <p className="exercise-instruction">{instruction}</p>
      </div>

      <div className="pe-pronunciation-card">
        <div className="pe-word-to-pronounce">
          <p className="pe-word-display">{currentWord?.text}</p>
          <p className="pe-phonetic-display">{currentWord?.phonetic}</p>
        </div>
        <div className="pe-pronunciation-controls">
          <button className="pe-speak-btn" onClick={() => speak(currentWord.text)} title="Escuchar">
            <Volume2 size={24} />
          </button>
          <button 
            className={`pe-record-btn ${isListening ? 'recording' : ''}`} 
            onClick={handleToggleListening}
            title="Grabar tu voz"
          >
            {isListening ? <Square size={24} /> : <Mic size={24} />}
          </button>
        </div>
      </div>
      
      {transcript && (
        <div className={`pe-feedback-card ${result || ''}`}>
          <div className="pe-feedback-header">Your Attempt:</div>
          <p className="pe-transcript-text">"{transcript}"</p>
          {result === 'correct' && <div className="pe-feedback-result correct">Perfect!</div>}
          {result === 'incorrect' && <div className="pe-feedback-result incorrect">Please try again. ({failedAttempts})</div>}
        </div>
      )}

      <div className="pe-navigation-controls">
        <button className="exercise-action-button" onClick={handlePrevious} disabled={currentWordIndex === 0}>
          <ArrowLeft size={20} /> Back
        </button>
        <div className="pe-word-counter">{currentWordIndex + 1} / {words.length}</div>
        <button className="exercise-action-button" onClick={handleNext} disabled={result !== 'correct'}>
          {currentWordIndex === words.length - 1 ? (<>Finish <CheckCircle2 size={20} /></>) : (<>Next <ArrowRight size={20} /></>)}
        </button>
      </div>
    </div>
  );
}

export default PronunciationExercise;